import { PROCESS_FILE_FAIL } from '@axa-asia/emma-stylesheet/lib/components/EmmaMolecules/EmmaUploadCard';

import { ScopedStore } from '../store';
import {
  ADD_FILE_FAIL,
  ADD_FILE_REQUEST,
  ADD_FILE_SUCCESS,
  AddFileFailPayload,
  AddFileRequestPayload,
  AddFileSuccessPayload,
  CLEAN_ALL_ERROR_FILES,
  CleanAllErrorFilesPayload,
  DELETE_FILE,
  DeleteFilePayload,
  INCREMENT_UPLOAD_DOCUMENT_SEQUENCE,
  RETRY_UPLOAD,
  RetryUploadPayload,
  UploadDocumentAction,
} from './actions';
import { getPureSubmissionField } from '@axa-asia/utils';
import { FeatureName } from '@/types/common';

const uploadDocumentReducer = (state: ScopedStore, action: UploadDocumentAction): ScopedStore => {
  switch (action.type) {
    case ADD_FILE_REQUEST:
      return onAddFileRequest(state, action);
    case ADD_FILE_SUCCESS:
      return onAddFileSuccess(state, action);
    case ADD_FILE_FAIL:
      return onAddFileFail(state, action);
    case DELETE_FILE:
      return onDeleteFile(state, action);
    case CLEAN_ALL_ERROR_FILES:
      return cleanAllErrorFiles(state, action);
    case INCREMENT_UPLOAD_DOCUMENT_SEQUENCE:
      return incrementUploadDocumentSequence(state, action);
    case RETRY_UPLOAD:
      return onRetryUpload(state, action);
    default:
      return state;
  }
};

const onAddFileRequest = (
  state: ScopedStore,
  action: { payload: AddFileRequestPayload },
) => {
  const { file, featureName, submissionField, sequence } = action.payload;

  const uploadedDocumentsList =
    state.uploadedDocuments?.[featureName]?.[submissionField] || [];

  const submissionFieldDocuments = [
    ...uploadedDocumentsList,
    {
      ...file,
      loading: true,
      sequence,
      ordering: sequence,
    },
  ];

  return {
    ...state,
    uploadedDocuments: {
      ...state.uploadedDocuments,
      [featureName]: {
        ...(state.uploadedDocuments?.[featureName] || {}),
        [submissionField]: submissionFieldDocuments,
      },
    },
  };
};

const onAddFileSuccess = (
  state: ScopedStore,
  action: { payload: AddFileSuccessPayload },
) => {
  const { featureName, submissionField, sequence, documentTitle } =
    action.payload;
  const uploadedDocumentsList =
    state.uploadedDocuments?.[featureName]?.[submissionField] || [];

  const submissionFieldDocuments = uploadedDocumentsList.map((doc) =>
    doc.sequence === sequence
      ? { ...doc, file: { ...doc.file, documentTitle }, loading: false }
      : doc,
  );

  return {
    ...state,
    uploadedDocuments: {
      ...state.uploadedDocuments,
      [featureName]: {
        ...state.uploadedDocuments[featureName],
        [submissionField]: submissionFieldDocuments,
      },
    },
  };
};

const onRetryUpload = (
  state: ScopedStore,
  action: { payload: RetryUploadPayload },
) => {
  const { featureName, submissionField, originalSequence, newSequence } =
    action.payload;

  const uploadedDocumentsList =
    state.uploadedDocuments?.[featureName]?.[submissionField] || [];

  const submissionFieldDocuments = uploadedDocumentsList.map((doc) =>
    doc.sequence === originalSequence
      ? {
          ...doc,
          loading: true,
          error: null,
          sequence: newSequence,
        }
      : doc,
  );

  return {
    ...state,
    uploadedDocuments: {
      ...state.uploadedDocuments,
      [featureName]: {
        ...state.uploadedDocuments[featureName],
        [submissionField]: submissionFieldDocuments,
      },
    },
  };
};

const onAddFileFail = (
  state: ScopedStore,
  action: { payload: AddFileFailPayload },
) => {
  const { featureName, submissionField, sequence, error } = action.payload;
  const uploadedDocumentsList =
    state.uploadedDocuments?.[featureName]?.[submissionField] || [];

  const submissionFieldDocuments = uploadedDocumentsList.map((doc) =>
    doc.sequence === sequence
      ? {
          ...doc,
          loading: false,
          failCount: (doc.failCount || 0) + 1,
          error: error || PROCESS_FILE_FAIL,
        }
      : doc,
  );

  return {
    ...state,
    uploadedDocuments: {
      ...state.uploadedDocuments,
      [featureName]: {
        ...state.uploadedDocuments[featureName],
        [submissionField]: submissionFieldDocuments,
      },
    },
  };
};

const onDeleteFile = (
  state: ScopedStore,
  action: { payload: DeleteFilePayload },
) => {
  const { featureName, submissionField, sequence } = action.payload;
  const uploadedDocumentsList =
    state.uploadedDocuments?.[featureName]?.[submissionField] || [];

  const submissionFieldDocuments = uploadedDocumentsList.filter(
    (doc) => doc.sequence !== sequence,
  );

  return {
    ...state,
    uploadedDocuments: {
      ...state.uploadedDocuments,
      [featureName]: {
        ...state.uploadedDocuments[featureName],
        [submissionField]: submissionFieldDocuments,
      },
    },
  };
};

const cleanAllErrorFiles = (
  state: ScopedStore,
  action: { payload: CleanAllErrorFilesPayload },
) => {
  const { featureName, submissionField } = action.payload;
  const uploadedDocumentsList =
    state.uploadedDocuments?.[featureName]?.[submissionField] || [];

  const submissionFieldDocuments = uploadedDocumentsList.filter(
    (doc) => !doc.error,
  );

  return {
    ...state,
    uploadedDocuments: {
      ...state.uploadedDocuments,
      [featureName]: {
        ...state.uploadedDocuments[featureName],
        [submissionField]: submissionFieldDocuments,
      },
    },
  };
};

export const incrementUploadDocumentSequence = (
  state: ScopedStore,
  action: {
    payload: {
      featureName: FeatureName;
      submissionField: string;
    };
  },
) => {
  const { featureName, submissionField } = action.payload;
  const pureSubmissionField = getPureSubmissionField(submissionField);

  const uploadedDocumentsSequence =
    state.uploadedDocumentsSequence?.[featureName]?.[pureSubmissionField] || 0;

  return {
    ...state,
    uploadedDocumentsSequence: {
      ...state.uploadedDocumentsSequence,
      [featureName]: {
        ...state.uploadedDocumentsSequence[featureName],
        [pureSubmissionField]: uploadedDocumentsSequence + 1,
      },
    },
  };
};

export default uploadDocumentReducer;
